import { formatDateTime } from "@utils/formatter";

export interface StatusTimelineEntry<S extends number> {
    id: string;
    // Null for the entry that created the record.
    fromStatus: S | null;
    toStatus: S;
    changedAt: string;
    note: string | null;
    // Who made the change, shown after its date; nothing when omitted, null
    // or only spaces.
    changedBy?: string | null;
}

interface StatusTimelineProps<S extends number> {
    // Shown in the order given: the client API sends newest first, the
    // order API oldest first.
    entries: StatusTimelineEntry<S>[];
    labelOf: (status: S) => string | undefined;
    // What a missing note reads as; nothing when omitted.
    missingNote?: string;
}

// The admin status timeline (review finding 9): an order's audit trail and a
// client's status history. A status with no label (a legacy value, or one
// added later) reads as its number rather than taking the page down (finding
// 7).
export const StatusTimeline = <S extends number>({
    entries,
    labelOf,
    missingNote,
}: StatusTimelineProps<S>) => {
    if (entries.length === 0) {
        return (
            <p className="text-sm text-neutral/70">
                No status changes recorded yet.
            </p>
        );
    }

    const label = (status: S): string => labelOf(status) ?? String(status);

    return (
        <ol className="border-l border-base-300 pl-4">
            {entries.map((entry) => (
                <li key={entry.id} className="mb-4 last:mb-0">
                    <p className="font-semibold">
                        {entry.fromStatus !== null
                            ? `${label(entry.fromStatus)} → ${label(entry.toStatus)}`
                            : `Created as ${label(entry.toStatus)}`}
                    </p>
                    <p className="text-sm text-neutral/70">
                        <time dateTime={entry.changedAt}>
                            {formatDateTime(entry.changedAt)}
                        </time>
                        {entry.changedBy?.trim() &&
                            ` by ${entry.changedBy.trim()}`}
                    </p>
                    {entry.note ? (
                        <p className="text-sm">{entry.note}</p>
                    ) : (
                        missingNote && (
                            <p className="text-sm text-neutral/70">
                                {missingNote}
                            </p>
                        )
                    )}
                </li>
            ))}
        </ol>
    );
};
